import { PushNotifications } from "@capacitor/push-notifications";
import { Text, Title, useModalState } from "@clipboard-health/ui-components";
import { DialogContent, Stack, Switch } from "@mui/material";
import { CommonDialog, isCapacitorPlatform } from "@src/appV2/lib";
import { openNativeSettings } from "@src/appV2/lib/NativeSettings/native-settings";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useEffect, useState } from "react";

import { Button } from "../components/Button";

interface PreferenceOnUpdateParams {
  key: string;
  value: boolean;
}

interface PreferenceItemProps {
  name?: string;
  caption?: string;
  initialValue?: boolean;
  preferenceKey: string;
  testId?: string;
  isBusy?: boolean;
  confirmAlertProps?: {
    header?: string;
    subHeader?: string;
    buttonProps?: {
      positiveName?: string;
      negativeName?: string;
    };
  };
  onUpdate?: (newValue: PreferenceOnUpdateParams) => Promise<void>;
}

export function PreferenceItem(params: PreferenceItemProps) {
  const {
    name,
    caption,
    preferenceKey,
    testId,
    isBusy,
    initialValue,
    onUpdate,
    confirmAlertProps,
  } = params;
  const showConfirmationDialog = useModalState();
  const showPushSettingsConfirmationDialog = useModalState();
  const [isChecked, setIsChecked] = useState<boolean | undefined>(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Updates toggle state based on change of prop initialValue
   */
  useEffect(() => {
    setIsChecked(initialValue);
  }, [initialValue]);

  /**
   * Handles toggle change event
   */
  async function onToggleChange() {
    if (isBusy ?? initialValue === !isChecked) {
      return;
    }

    setIsLoading(true);

    if (isChecked) {
      await onUpdate?.({
        key: preferenceKey,
        value: !isChecked,
      });
      setIsLoading(false);
    } else {
      showConfirmationDialog.openModal();
    }
  }

  /**
   * Handles confirmation alert dismiss event when option is selected
   * @param isYesClicked of type boolean to know if user clicked Yes option
   */
  async function onCloseAlert(isYesClicked: boolean) {
    showConfirmationDialog.closeModal();

    if (isYesClicked) {
      if (preferenceKey === "push") {
        const { receive } = isCapacitorPlatform()
          ? await PushNotifications.requestPermissions()
          : { receive: "granted" };

        if (receive === "denied") {
          showPushSettingsConfirmationDialog.openModal();
        } else {
          await onUpdate?.({
            key: preferenceKey,
            value: true,
          });
        }
      } else {
        await onUpdate?.({
          key: preferenceKey,
          value: true,
        });
      }
    }

    setIsLoading(false);
  }

  /**
   * Handles push settings confirmation alert dismiss event when option is selected
   * @param isYesClicked of type boolean to know if user clicked Yes option
   */
  async function onClosePushSettingsAlert(isYesClicked: boolean) {
    showPushSettingsConfirmationDialog.closeModal();

    if (isYesClicked && isCapacitorPlatform()) {
      await openNativeSettings({ option: "settings" });
    }
  }

  return (
    <>
      <Stack
        direction="row"
        spacing={5}
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Stack>
          <Text>{name}</Text>
          {caption && <Text variant="caption">{caption}</Text>}
        </Stack>
        <Switch
          checked={isChecked}
          value={isChecked?.toString()}
          disabled={isBusy ?? isLoading}
          data-testid={testId}
          onChange={async () => {
            await onToggleChange();
          }}
        />
      </Stack>

      <CommonDialog
        modalState={showConfirmationDialog}
        title={<Title component="h4">{confirmAlertProps?.header}</Title>}
        actions={
          <DialogFooter orientation="horizontal">
            <Button
              variant="outlined"
              onClick={async () => {
                await onCloseAlert(false);
              }}
            >
              {confirmAlertProps?.buttonProps?.negativeName}
            </Button>
            <Button
              onClick={async () => {
                await onCloseAlert(true);
              }}
            >
              {confirmAlertProps?.buttonProps?.positiveName}
            </Button>
          </DialogFooter>
        }
      >
        <DialogContent>
          <Text>{confirmAlertProps?.subHeader}</Text>
        </DialogContent>
      </CommonDialog>

      {preferenceKey === "push" && (
        <CommonDialog
          modalState={showPushSettingsConfirmationDialog}
          title={<Title component="h4">Sign up for Push Notifications</Title>}
          actions={
            <DialogFooter orientation="horizontal">
              <Button
                variant="outlined"
                onClick={async () => {
                  await onClosePushSettingsAlert(false);
                }}
              >
                No Thanks
              </Button>
              <Button
                onClick={async () => {
                  await onClosePushSettingsAlert(true);
                }}
              >
                Go to settings
              </Button>
            </DialogFooter>
          }
        >
          <DialogContent>
            <Text>Please go to settings to allow Clipboard to send you push notifications</Text>
          </DialogContent>
        </CommonDialog>
      )}
    </>
  );
}
